import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { ToneOption } from '../services/scheduleGeneratorService';
import ChoiceQuestion from '../../../components/common/ChoiceQuestion';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

interface ToneSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  summary: string | null;
  options: ToneOption[];
  isLoading: boolean;
  onSelect: (intention: string) => void;
  draftDate: string;
  onDateChange: (date: string) => void;
}

function SummarySkeleton() {
  return (
    <div className="px-4 py-4 bg-neutral-50 border-b border-neutral-100" aria-hidden="true">
      <div className="h-3 rounded animate-shimmer w-4/5 mb-2" />
      <div className="h-3 rounded animate-shimmer w-3/5" />
    </div>
  );
}

function OptionsSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading tone options">
      {[0, 1, 2].map(i => (
        <div key={i} className="px-4 py-3 border-b border-neutral-100" aria-hidden="true">
          <div className="h-4 rounded animate-shimmer w-3/5 mb-2" />
          <div className="h-3 rounded animate-shimmer w-4/5" />
        </div>
      ))}
      <p className="text-xs text-neutral-400 text-center py-3">Reading your week…</p>
    </div>
  );
}

const ToneSelectionModal: React.FC<ToneSelectionModalProps> = ({
  isOpen,
  onClose,
  summary,
  options,
  isLoading,
  onSelect,
  draftDate,
  onDateChange,
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const tomorrowStr = format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const isDateToday = !draftDate || draftDate === todayStr;

  const handleDateToggle = (targetDate: string) => {
    if (targetDate === draftDate) return;
    onDateChange(targetDate);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // 手機：底部滑入；桌機：輕微下移 + fade
  const cardVariants = isDesktop
    ? {
        initial: { opacity: 0, y: -8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -8 },
      }
    : {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
      };

  const cardTransition = isDesktop
    ? ({ duration: 0.2, ease: 'easeOut' } as const)
    : ({ type: 'spring', damping: 30, stiffness: 300 } as const);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-modal-nested flex justify-center items-end sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label="Choose your approach for today"
        >
          {/* backdrop */}
          <motion.div
            className="absolute inset-0 bg-neutral-900 bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          {/* card */}
          <motion.div
            className="relative bg-white rounded-t-xl md:rounded-lg w-full sm:max-w-md sm:mx-auto shadow-xl flex flex-col"
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={cardTransition}
          >
            {/* drag handle */}
            <div className="sm:hidden w-full flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-neutral-300 rounded-full" />
            </div>

            {/* header */}
            <div className="flex justify-between items-center px-4 pt-3 pb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-neutral-800">Day Draft</h2>
                <div className="flex items-center bg-neutral-100 rounded-full p-0.5 text-xs font-medium">
                  <button
                    onClick={() => handleDateToggle(todayStr)}
                    className={`px-2.5 py-0.5 rounded-full transition-colors ${
                      isDateToday
                        ? 'bg-white text-neutral-800 shadow-sm'
                        : 'text-neutral-400 hover:text-neutral-600'
                    }`}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => handleDateToggle(tomorrowStr)}
                    className={`px-2.5 py-0.5 rounded-full transition-colors ${
                      !isDateToday
                        ? 'bg-white text-neutral-800 shadow-sm'
                        : 'text-neutral-400 hover:text-neutral-600'
                    }`}
                  >
                    Tomorrow
                  </button>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-neutral-400 hover:text-neutral-600 transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* summary */}
            {isLoading ? (
              <SummarySkeleton />
            ) : summary ? (
              <div className="px-4 py-4 bg-neutral-50 border-b border-neutral-100">
                <p className="text-sm text-neutral-700 leading-relaxed">{summary}</p>
              </div>
            ) : null}

            {/* question + options */}
            {isLoading ? (
              <OptionsSkeleton />
            ) : (
              <ChoiceQuestion
                question="How do you want to approach today?"
                options={options}
                onSelect={onSelect}
              />
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ToneSelectionModal;
